import { z } from 'zod';

// ==================== AG Grid SSRM Column ====================

export const ColumnSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  field: z.string(),
  aggFunc: z.string().optional(),
  bucketing: z.enum(['day', 'week', 'month', 'quarter', 'year']).optional(),
});

// ==================== Sort Model ====================

export const SortModelItemSchema = z.object({
  colId: z.string(),
  sort: z.enum(['asc', 'desc']),
});

// ==================== Filter Models ====================

export const TextFilterSchema = z.object({
  filterType: z.literal('text'),
  type: z.enum([
    'contains',
    'notContains',
    'equals',
    'notEqual',
    'startsWith',
    'endsWith',
    'blank',
    'notBlank',
  ]),
  filter: z.string().optional(),
});

export const DateFilterSchema = z.object({
  filterType: z.literal('date'),
  type: z.enum([
    'equals',
    'notEqual',
    'lessThan',
    'greaterThan',
    'lessThanOrEqual',
    'greaterThanOrEqual',
    'inRange',
    'blank',
    'notBlank',
    'yesterday',
    'lastWeek',
    'lastMonth',
    'thisMonth',
    'lastQuarter',
    'thisQuarter',
    'lastYear',
    'thisYear',
    'last12Months',
    'allTime',
  ]),
  dateFrom: z.string().nullable().optional(),
  dateTo: z.string().nullable().optional(),
});

export const NumberFilterSchema = z.object({
  filterType: z.literal('number'),
  type: z.enum([
    'equals',
    'notEqual',
    'greaterThan',
    'greaterThanOrEqual',
    'lessThan',
    'lessThanOrEqual',
    'inRange',
    'blank',
    'notBlank',
  ]),
  // It should be number, but our backend working with string values for number filters, so we need to support both types here
  filter: z.union([z.number(), z.string()]).optional(),
  filterTo: z.union([z.number(), z.string()]).nullable().optional(),
});

export const CombinedFilterSchema = z.object({
  filterType: z.enum(['text', 'number', 'date']),
  operator: z.enum(['AND', 'OR']),
  conditions: z.array(
    z.union([TextFilterSchema, NumberFilterSchema, DateFilterSchema]),
  ),
});

export const SetFilterSchema = z.object({
  filterType: z.literal('set'),
  values: z.array(z.string()),
});

export const FilterModelValueSchema = z.union([
  TextFilterSchema,
  DateFilterSchema,
  NumberFilterSchema,
  CombinedFilterSchema,
  SetFilterSchema,
]);

export const FilterModelSchema = z.record(z.string(), FilterModelValueSchema);

// ==================== Request ====================

export const GridDataRequestSchema = z.object({
  startRow: z.number(),
  endRow: z.number(),
  rowGroupCols: z.array(ColumnSchema),
  valueCols: z.array(ColumnSchema),
  pivotCols: z.array(ColumnSchema).optional().default([]),
  pivotMode: z.boolean().optional().default(false),
  groupKeys: z.array(z.string()),
  sortModel: z.array(SortModelItemSchema).optional().default([]),
  filterModel: FilterModelSchema.optional().default({}),
  needsGrandTotal: z.boolean().optional(),
});

// ==================== Response ====================

export const GridDataResponseSchema = z.object({
  rowData: z.array(z.record(z.string(), z.unknown())),
  rowCount: z.number(),
  pivotResultFields: z.array(z.string()).optional(),
  grandTotalData: z.record(z.string(), z.unknown()).nullable().optional(),
});

// ==================== AG Grid Dimensions POC ====================

export const GridDimensionSchema = z.object({
  name: z.string(),
  title: z.string(),
  type: z.string(),
});

export const GridDimensionsResponseSchema = z.object({
  dimensions: z.array(GridDimensionSchema),
});

// ==================== AG Grid Metrics POC ====================

export const GridMetricSchema = z.object({
  name: z.string(),
  title: z.string(),
  type: z.string(),
  category: z.string(),
  emptyValue: z.unknown().nullable().optional(),
});

export const GridMetricsResponseSchema = z.object({
  metrics: z.array(GridMetricSchema),
});
